import { isEmpty, isNil } from 'lodash';
import { OVERALL_SLICE_KEY } from '../../components/Chart/constants/utils';
import { IDataTableSliceFieldsFragmentWithComparison } from '../../components/Chart/interfaces';
import { IDataTableColumn } from '../apollo/graphqlSchema';
import { IDataTableAnalysisData } from '../apollo/responseTypes';
import { MaybeNil } from '../interfaces';

interface IDataTableColumnWithComparison extends IDataTableColumn {
  isComparison?: boolean;
}

// processes the comparison columns to extend the columns with an isComparison flag
// if the comparison data is empty, we add null values to the comparison columns to simulate the comparison columns
const processComparisonColumns = (
  primaryData: IDataTableAnalysisData,
  comparisonData: IDataTableAnalysisData,
): IDataTableColumnWithComparison[] => {
  // Create comparison columns even if empty
  // This is done by adding a null value to the comparison columns
  return isEmpty(comparisonData.columns)
    ? primaryData.columns.map((col) => ({
        ...col,
        values: [null],
        isComparison: true,
      }))
    : comparisonData.columns.map((col) => ({ ...col, isComparison: true }));
};

// gets all the unique slices from the primary and comparison data
const getUniqueSlices = (
  primaryData: IDataTableAnalysisData,
  comparisonData: IDataTableAnalysisData,
) => {
  const uniqueSliceKeys = new Set([
    ...primaryData.slices.map((slice) => slice.key),
    ...comparisonData.slices.map((slice) => slice.key),
  ]);

  const uniqueSlices = [];

  for (const key of uniqueSliceKeys) {
    const comparisonSlice = comparisonData.slices.find(
      (slice) => slice.key === key,
    );
    const primarySlice = primaryData.slices.find((slice) => slice.key === key);
    if (comparisonSlice) {
      uniqueSlices.push({ ...comparisonSlice, isComparison: true });
    } else if (primarySlice) {
      uniqueSlices.push({ ...primarySlice, isComparison: false });
    }
  }

  return uniqueSlices;
};

const processOverallSlice = (
  slice: IDataTableSliceFieldsFragmentWithComparison,
  measureKey: string,
  comparisonColumns: IDataTableColumnWithComparison[],
  primaryData: IDataTableAnalysisData,
): IDataTableColumnWithComparison[] => {
  const columns: IDataTableColumnWithComparison[] = [];

  const comparisonColumn = comparisonColumns.find(
    (col, index) =>
      col.measureKey === measureKey && slice.measures.includes(index),
  );

  const primaryColumn = primaryData.columns.find(
    (col, index) =>
      col.measureKey === measureKey && slice.measures.includes(index),
  );

  if (comparisonColumn) {
    columns.push({ ...comparisonColumn, isComparison: true });
  }
  if (primaryColumn) {
    columns.push({ ...primaryColumn, isComparison: false });
  }

  return columns;
};

const findMatchingColumn = (
  slice: IDataTableSliceFieldsFragmentWithComparison,
  mergedColumn: IDataTableColumnWithComparison,
  comparisonColumns: IDataTableColumnWithComparison[],
  primaryData: IDataTableAnalysisData,
) => {
  if (slice.isComparison) {
    return comparisonColumns.find(
      (comparisonColumn, colIndex) =>
        comparisonColumn.measureKey === mergedColumn.measureKey &&
        slice.measures.includes(colIndex),
    );
  }

  return primaryData.columns.find(
    (primaryColumn, colIndex) =>
      primaryColumn.measureKey === mergedColumn.measureKey &&
      slice.measures.includes(colIndex),
  );
};

const processRegularSlice = (
  slice: IDataTableSliceFieldsFragmentWithComparison,
  measureKey: string,
  comparisonColumns: IDataTableColumnWithComparison[],
  primaryData: IDataTableAnalysisData,
): IDataTableColumnWithComparison[] => {
  const columns: IDataTableColumnWithComparison[] = [];

  if (slice.isComparison) {
    const comparisonColumn = comparisonColumns.find(
      (col, colIndex) =>
        col.measureKey === measureKey && slice.measures.includes(colIndex),
    );

    if (comparisonColumn) {
      columns.push({ ...comparisonColumn, isComparison: true });

      const primaryColumn = primaryData.columns.find(
        (col, _) =>
          col.measureKey === measureKey && col.key === comparisonColumn?.key,
      );

      if (primaryColumn) {
        columns.push({ ...primaryColumn, isComparison: false });
      } else {
        columns.push({
          ...comparisonColumn,
          values: [null],
          isComparison: false,
        });
      }
    }
  } else {
    const primaryColumn = primaryData.columns.find(
      (col, colIndex) =>
        col.measureKey === measureKey && slice.measures.includes(colIndex),
    );

    if (primaryColumn) {
      const comparisonColumn = comparisonColumns.find(
        (col, _) =>
          col.measureKey === measureKey && col.key === primaryColumn?.key,
      );

      if (comparisonColumn) {
        columns.push({ ...comparisonColumn, isComparison: true });
      } else {
        columns.push({ ...primaryColumn, values: [null], isComparison: true });
      }

      columns.push({ ...primaryColumn, isComparison: false });
    }
  }

  return columns;
};

export const mergeDataTableComparisonData = ({
  primaryData,
  comparisonData,
}: {
  primaryData: IDataTableAnalysisData;
  comparisonData: MaybeNil<IDataTableAnalysisData>;
}): IDataTableAnalysisData => {
  if (isNil(comparisonData)) {
    return primaryData;
  }

  const comparisonColumns = processComparisonColumns(
    primaryData,
    comparisonData,
  );

  const measureKeys = Array.from(
    new Set([
      ...primaryData.columns.map((column) => column.measureKey),
      ...comparisonColumns.map((column) => column.measureKey),
    ]),
  );

  const uniqueSlices = getUniqueSlices(primaryData, comparisonData);
  const slicesToProcess =
    uniqueSlices.length > 0
      ? uniqueSlices
      : primaryData.base
        ? [primaryData.base]
        : [];

  const mergedColumns: IDataTableColumnWithComparison[] = [];

  // Process all measure keys and slices
  for (const measureKey of measureKeys) {
    for (const slice of slicesToProcess) {
      const newColumns =
        slice.key === OVERALL_SLICE_KEY
          ? processOverallSlice(
              slice,
              measureKey,
              comparisonColumns,
              primaryData,
            )
          : processRegularSlice(
              slice,
              measureKey,
              comparisonColumns,
              primaryData,
            );

      mergedColumns.push(...newColumns);
    }
  }

  // Create merged slices
  const mergedSlices = uniqueSlices.map((slice) => ({
    ...slice,
    measures: mergedColumns
      .filter((mergedColumn) => {
        const matchingColumn = findMatchingColumn(
          slice,
          mergedColumn,
          comparisonColumns,
          primaryData,
        );
        return mergedColumn.key === matchingColumn?.key;
      })
      .map((column) => mergedColumns.indexOf(column)),
  }));

  // Create merged base
  const mergedBase = primaryData.base
    ? {
        ...primaryData.base,
        measures: mergedColumns.map((_, index) => index),
      }
    : null;

  return {
    ...primaryData,
    slices: mergedSlices,
    columns: mergedColumns,
    base: mergedBase,
  };
};
